import {
  getUserRoleLabel,
  OvertimeEntry,
  UserRole,
} from "@employee-management/common";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  ChevronDown,
  FolderOpen,
  Home,
  LogOut,
  Menu,
  Settings,
  Shield,
  User,
  Users,
  Cog,
  Clock,
  ChevronRight,
  FileSpreadsheet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useAuth } from "@/lib/auth-context";
import { useTheme } from "@/lib/theme-provider";
import { useQuery } from "@tanstack/react-query";
import { apiService } from "@/lib/api-service";
import clsx from "clsx";
import { useState } from "react";
import { motion } from "motion/react";
import { useMediaQuery } from "usehooks-ts";
import { Drawer } from "vaul";

interface MenuItem {
  title: string;
  href: string;
  icon: LucideIcon;
  requiredRole?: UserRole;
}

const menuItems: MenuItem[] = [
  {
    title: "Dashboard",
    href: "/home",
    icon: Home,
  },
  {
    title: "Pending Approvals",
    href: "/pending-approvals",
    icon: Clock,
    requiredRole: UserRole.MANAGER,
  },
  {
    title: "Projects",
    href: "/projects",
    icon: FolderOpen,
  },
  {
    title: "Reports",
    href: "/reports",
    icon: FileSpreadsheet,
    requiredRole: UserRole.SUPERADMIN,
  },
  {
    title: "Employees",
    href: "/employees",
    icon: Users,
    requiredRole: UserRole.MANAGER,
  },
  {
    title: "User Management",
    href: "/users",
    icon: Shield,
    requiredRole: UserRole.SUPERADMIN,
  },
  {
    title: "App Settings",
    href: "/app-settings",
    icon: Cog,
    requiredRole: UserRole.SUPERADMIN,
  },
];

const DEFAULT_BOTTOM_NAV_ITEMS = menuItems.filter(
  (item) => item.title === "Dashboard" || item.title === "Projects"
);

export function Sidebar() {
  const router = useRouterState();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { user, logout, logoutAll, hasRole } = useAuth();

  const isSmallScreen = useMediaQuery("(max-width: 420px)");

  const currentPath = router.location.pathname;

  // Filter menu items based on user role
  const filteredMenuItems = menuItems.filter((item) => {
    if (item.requiredRole) {
      return hasRole(item.requiredRole);
    }
    return true;
  });

  const { data: pendingEntries } = useQuery({
    queryKey: ["overtime", "pending"],
    queryFn: () => apiService.get<OvertimeEntry[]>("/overtime/pending"),
  });

  const [isBottomNavOpen, setIsBottomNavOpen] = useState(false);

  const onNavElClick = () => {
    setIsBottomNavOpen(false);
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex items-center px-6 py-8">
        <img
          src={
            isDarkMode
              ? "/assets/brand/ertqa-logo-white.png"
              : "/assets/brand/ertqa-logo-black.png"
          }
          alt="Employee Management"
          className="w-auto object-contain"
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-4">
        {filteredMenuItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentPath.startsWith(item.href);

          return (
            <Link
              key={item.href}
              to={item.href}
              className={`flex items-center space-x-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              }`}
              onClick={onNavElClick}
            >
              <Icon className="h-4 w-4" />
              <span>{item.title}</span>
              {item.title === "Pending Approvals" &&
                Number(pendingEntries?.length) > 0 && (
                  <span
                    className={clsx(
                      "transition",
                      "!ml-auto px-1.5 py-0.5 rounded-md !text-xs ",
                      !isActive
                        ? "bg-primary text-primary-foreground"
                        : "bg-background text-foreground"
                    )}
                  >
                    {pendingEntries?.length}
                  </span>
                )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t p-4">
        {/* User Info and Logout */}
        <div className="space-y-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start py-8"
              >
                <User className="mr-2 h-6 w-6" />
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium">{user?.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {user?.role && getUserRoleLabel(user.role)}
                  </span>
                </div>

                <ChevronDown className="h-4 w-4 ml-auto" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center">
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Profile Settings</span>

                  <ChevronRight className="ml-auto h-4 w-4" />
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Logout</span>
                <ChevronRight className="ml-auto h-4 w-4" />
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logoutAll}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Logout All Devices</span>
                <ChevronRight className="ml-auto h-4 w-4" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center justify-center mt-4">
          <ThemeToggle />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col border-r bg-background">
        <SidebarContent />
      </div>

      {/* Mobile Sidebar */}
      <Drawer.Root open={isBottomNavOpen} onOpenChange={setIsBottomNavOpen}>
        <Drawer.Portal>
          <Drawer.Content className="flex flex-col h-fit fixed bottom-0 left-0 right-0 outline-none z-[50]">
            <Drawer.Handle />
            <div className="bg-background mt-2 z-[40]">
              <SidebarContent />
            </div>
          </Drawer.Content>
          <Drawer.Overlay className="fixed inset-0 bg-black/80" />
        </Drawer.Portal>
      </Drawer.Root>

      <div className="fixed bottom-0 left-0 right-0 lg:hidden z-[40]">
        <div className="flex items-center justify-between gap-3 px-3 py-2 border-t bg-background/80 backdrop-blur-md shadow-lg supports-[backdrop-filter]:bg-background/60">
          <div className="flex flex-1 justify-center">
            {(() => {
              const columns = DEFAULT_BOTTOM_NAV_ITEMS.length || 1;
              const activeIndexRaw = DEFAULT_BOTTOM_NAV_ITEMS.findIndex(
                (item) => currentPath.startsWith(item.href)
              );
              const activeIndex = activeIndexRaw === -1 ? 0 : activeIndexRaw;
              return (
                <div
                  aria-label="Primary navigation"
                  className="relative rounded-full bg-muted/50 ring-1 ring-border p-1"
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                  }}
                >
                  <motion.div
                    layout
                    className="absolute top-1 bottom-1 rounded-full bg-background shadow-sm ring-1 ring-border"
                    style={{ width: `${100 / columns}%` }}
                    animate={{ left: `${(100 / columns) * activeIndex}%` }}
                    transition={{
                      type: "spring",
                      stiffness: 320,
                      damping: 26,
                      mass: 0.6,
                    }}
                  />
                  {DEFAULT_BOTTOM_NAV_ITEMS.map((item, index) => {
                    const isActive = index === activeIndex;
                    return (
                      <motion.button
                        key={item.title}
                        type="button"
                        onClick={() => navigate({ to: item.href })}
                        aria-current={isActive ? "page" : undefined}
                        className={clsx(
                          "relative z-10 inline-flex items-center justify-center gap-2 px-3",
                          isSmallScreen ? "h-10" : "h-11",
                          "rounded-full text-sm font-medium transition-colors",
                          isActive
                            ? "text-foreground"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                        whileTap={{ scale: 0.98 }}
                      >
                        <item.icon className="h-5 w-5" />
                        <span className="max-[380px]:hidden">{item.title}</span>
                      </motion.button>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          <Button
            variant="default"
            onClick={() => setIsBottomNavOpen(true)}
            className={clsx(
              "rounded-full h-10 w-10 sm:h-11 sm:w-11 shadow-md ring-1 ring-border",
              "flex items-center justify-center"
            )}
            aria-label="Open menu"
            size={isSmallScreen ? "icon" : "default"}
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>
        <div className="pb-[max(0px,env(safe-area-inset-bottom))]" />
      </div>
    </>
  );
}
